import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { TableTitleBox } from '../../common/components';
import { useGerencialesStatistics } from './hooks/useGerencialesStatistics';
import {
  GERENCIALES_REPORT_OPTIONS,
  GERENCIALES_REPORT_TYPE,
  GERENCIALES_PAYMENTS_REPORT_TYPE,
  GERENCIALES_DEBT_BALANCE_REPORT_TYPE,
  GERENCIALES_PDF_REPORTS,
} from './constants/gerencialesConstants';
import GerencialesFilters from './components/GerencialesFilters';
import ContractDetailsReport from './components/contract-details/ContractDetailsReport';
import PaymentsReport from './components/payments-report/PaymentsReport';
import DebtBalanceReport from './components/debt-balance-report/DebtBalanceReport';
import { generateContractDetailsPdf, generatePaymentsReportPdf } from './utils/gerencialesPdfUtils';

const GerencialesStatistics = () => {
  const {
    loading,
    error,
    results,
    header,
    reportType,
    filters,
    reload,
    handleReportTypeChange,
    handleDateChange,
  } = useGerencialesStatistics();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPdfCooldown, setIsPdfCooldown] = useState(false);
  const pdfCooldownTimeoutRef = useRef(null);

  /**
   * Valida si hay datos para exportar.
   * @param {Object|null} reportResults - Resultados del reporte.
   * @returns {boolean} Indica si hay data disponible.
   */
  const hasReportData = (reportResults) => {
    if (!reportResults) return false;
    if (Array.isArray(reportResults)) return reportResults.length > 0;
    return Object.values(reportResults).some(
      (value) => Array.isArray(value) && value.length > 0
    );
  };

  /**
   * Maneja la descarga del PDF según el tipo de reporte seleccionado.
   */
  const handleDownloadPdf = async () => {
    if (!hasReportData(results)) return;

    setIsDownloading(true);
    try {
      // Generar PDF según el tipo de reporte
      switch (reportType) {
        case GERENCIALES_REPORT_TYPE:
          await generateContractDetailsPdf(results, header);
          break;
        case GERENCIALES_PAYMENTS_REPORT_TYPE:
          await generatePaymentsReportPdf(results, header);
          break;
        // Aquí se pueden agregar más casos para otros tipos de reportes
        default:
          console.warn(`Tipo de reporte no soportado: ${reportType}`);
      }
    } catch (error) {
      console.error('Error al generar el PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (pdfCooldownTimeoutRef.current) {
      clearTimeout(pdfCooldownTimeoutRef.current);
      pdfCooldownTimeoutRef.current = null;
    }

    if (!GERENCIALES_PDF_REPORTS.includes(reportType)) {
      setIsPdfCooldown(false);
      return;
    }

    if (loading || !hasReportData(results)) {
      setIsPdfCooldown(false);
      return;
    }

    setIsPdfCooldown(true);
    pdfCooldownTimeoutRef.current = setTimeout(() => {
      setIsPdfCooldown(false);
    }, 2500);

    return () => {
      if (pdfCooldownTimeoutRef.current) {
        clearTimeout(pdfCooldownTimeoutRef.current);
      }
    };
  }, [loading, reportType, results]);

  return (
    <Box>
      <TableTitleBox title="Estadisticas | Gerenciales" />
      <GerencialesFilters
        reportType={reportType}
        reportOptions={GERENCIALES_REPORT_OPTIONS}
        loading={loading}
        error={error}
        header={header}
        dateStart={filters.dateStart}
        dateEnd={filters.dateEnd}
        onDateChange={handleDateChange}
        onReportTypeChange={handleReportTypeChange}
        onReload={reload}
        onDownloadPdf={handleDownloadPdf}
        pdfCooldown={isPdfCooldown}
        disableDownload={
          !GERENCIALES_PDF_REPORTS.includes(reportType) ||
          !hasReportData(results) ||
          isDownloading ||
          isPdfCooldown
        }
      />
      {reportType === GERENCIALES_REPORT_TYPE && (
        <ContractDetailsReport loading={loading} results={results} header={header} />
      )}
      {reportType === GERENCIALES_PAYMENTS_REPORT_TYPE && (
        <PaymentsReport loading={loading} results={results} header={header} />
      )}
      {reportType === GERENCIALES_DEBT_BALANCE_REPORT_TYPE && (
        <DebtBalanceReport loading={loading} results={results} header={header} />
      )}
    </Box>
  );
};

export default GerencialesStatistics;
